import type { LocalizedText, NodeType } from "./data/schema";

export const greekAtlasRegionIds = [
  "primordial",
  "olympus",
  "mortal",
  "seas",
  "underworld",
] as const;
export const norseAtlasRegionIds = [
  "world-tree",
  "asgard",
  "giant-worlds",
  "binding-places",
  "doom-horizon",
  "heroic-lays",
] as const;
export const atlasRegionIds = [
  ...greekAtlasRegionIds,
  ...norseAtlasRegionIds,
] as const;
export type GreekAtlasRegionId = (typeof greekAtlasRegionIds)[number];
export type NorseAtlasRegionId = (typeof norseAtlasRegionIds)[number];
export type AtlasRegionId = (typeof atlasRegionIds)[number];
export type AtlasDatasetKey = "greek" | "norse";

export type AtlasReservationSourcePlan =
  | "greek-core"
  | "olympians"
  | "underworld"
  | "heracles"
  | "argonauts"
  | "norse-cosmology"
  | "norse-divine-order"
  | "norse-geirrod-journey"
  | "norse-poetic-mead"
  | "norse-thor"
  | "norse-baldr-loki"
  | "norse-ragnarok"
  | "norse-volsungs"
  | "norse-volund"
  | "norse-helgi-hundingsbane"
  | "norse-helgi-hjorvarthsson"
  | "norse-vafthruthnismol"
  | "norse-atlamol"
  | "norse-hervor-awakening"
  | "norse-lokasenna"
  | "norse-hymiskvitha"
  | "norse-alvissmol"
  | "norse-hyndluljoth"
  | "norse-harbarthsljoth"
  | "norse-grimnismol"
  | "norse-helgakvitha-hundingsbana-ii"
  | "norse-rigsthula"
  | "norse-guthrunarkvitha-i"
  | "norse-sigurtharkvitha-en-skamma"
  | "norse-helreith-brynhildar"
  | "norse-brot-sigurtharkvithu"
  | "norse-guthrunarkvitha-ii"
  | "norse-guthrunarkvitha-iii"
  | "norse-oddrunargratr"
  | "norse-atlakvitha"
  | "norse-guthrunarhvot"
  | "norse-hamthesmol"
  | "norse-drap-niflunga"
  | "norse-fafnismol"
  | "norse-sigrdrifumol"
  | "norse-skirnismol"
  | "norse-thrymskvitha"
  | "norse-reginsmol"
  | "norse-baldrs-draumar"
  | "norse-fra-dautha-sinfjotla"
  | "norse-gripisspo"
  | "norse-svipdagsmol"
  | "norse-grottasongr"
  | "norse-fenrir-extension";

export interface AtlasReservation {
  id: string;
  regionId: AtlasRegionId;
  type: NodeType;
  name: LocalizedText;
  summary: LocalizedText;
  plannedStoryIds: string[];
  sourcePlan: AtlasReservationSourcePlan;
}

const greekCurrentNodeIdsByRegion: Readonly<
  Record<GreekAtlasRegionId, readonly string[]>
> = {
  primordial: [
    "chaos",
    "gaia",
    "swallowing-children",
    "nyx",
    "iapetus",
    "pallas-titan",
    "titans",
    "eris",
    "titanomachy-event",
    "cyclopes",
    "eros",
    "tethys",
    "hecatoncheires",
    "cronus",
    "rhea",
    "uranus",
    "moirai",
    "menoetius",
    "mount-othrys",
    "atlas",
  ],
  olympus: [
    "mount-olympus",
    "athena",
    "kratos",
    "zeus",
    "artemis",
    "aphrodite",
    "hestia",
    "apollo",
    "metis",
    "bia",
    "demeter",
    "hermes",
    "zelus",
    "helios",
    "nike",
    "ares",
    "hephaestus",
    "hera",
  ],
  mortal: [
    "troy",
    "achilles",
    "telemachus",
    "ithaca",
    "trojan-war",
    "cassandra",
    "judgement-paris",
    "laertes",
    "penelope",
    "agamemnon",
    "return-ithaca",
    "arete",
    "hector",
    "apollo-plague",
    "paris",
    "alcinous",
    "contest-bow",
    "briseis",
    "aeneas",
    "nestor",
    "helen",
    "suitors",
    "odysseus",
    "iphigenia",
    "diomedes",
    "laocoon",
    "wooden-horse",
    "andromache",
    "patroclus",
    "sack-troy",
    "menelaus",
    "eurycleia",
    "chryses",
    "aulis",
    "epeius",
    "anticlea",
    "nausicaa",
    "priam",
    "eumaeus",
    "hecuba",
    "ajax",
  ],
  seas: [
    "poseidon",
    "aeaea",
    "thetis",
    "calypso",
    "blinding-polyphemus",
    "scylla",
    "oceanus",
    "aeolus",
    "polyphemus",
    "charybdis",
    "thrinacia",
    "storm-poseidon",
    "scheria",
    "pontus",
    "sirens",
    "laestrygonians",
    "ogygia",
    "circe",
    "nereus",
  ],
  underworld: [
    "underworld",
    "hades",
    "hecate",
    "descent-underworld",
    "tartarus",
    "thanatos",
    "persephone",
    "tiresias",
    "campe",
    "styx",
    "hypnos",
  ],
};

const norseCurrentNodeIdsByRegion: Readonly<
  Record<NorseAtlasRegionId, readonly string[]>
> = {
  "world-tree": ["svartalfheim"],
  asgard: ["odin", "tyr", "loki"],
  "giant-worlds": ["angrboda", "fenrir"],
  "binding-places": [
    "amsvartnir-lyngvi",
    "gleipnir",
    "binding-of-fenrir-event",
  ],
  "doom-horizon": ["ragnarok"],
  "heroic-lays": [],
};

const atlasLeadSlotIdsByDataset: Readonly<
  Record<AtlasDatasetKey, Partial<Record<AtlasRegionId, readonly string[]>>>
> = {
  greek: {},
  norse: {
    "world-tree": ["yggdrasil"],
    asgard: ["odin"],
    "giant-worlds": ["fenrir"],
    "binding-places": ["amsvartnir-lyngvi"],
    "doom-horizon": ["ragnarok"],
    "heroic-lays": ["sigurd"],
  },
};

const reserve = (
  id: string,
  regionId: AtlasRegionId,
  type: NodeType,
  zhHant: string,
  en: string,
  sourcePlan: AtlasReservationSourcePlan,
  plannedStoryIds: string[],
): AtlasReservation => ({
  id,
  regionId,
  type,
  name: { "zh-Hant": zhHant, en },
  summary: { "zh-Hant": "？？？", en: "???" },
  sourcePlan,
  plannedStoryIds,
});

export const ATLAS_RESERVATIONS: readonly AtlasReservation[] = [
  // Primordial foundations and the remaining succession/cosmic inventory.
  reserve("aether", "primordial", "deity", "埃忒耳", "Aether", "greek-core", [
    "olympians",
  ]),
  reserve("erebus", "primordial", "deity", "厄瑞玻斯", "Erebus", "greek-core", [
    "underworld",
  ]),
  reserve("hemera", "primordial", "deity", "赫墨拉", "Hemera", "greek-core", [
    "olympians",
  ]),
  reserve("coeus", "primordial", "deity", "科俄斯", "Coeus", "greek-core", [
    "olympians",
  ]),
  reserve("crius", "primordial", "deity", "克利俄斯", "Crius", "greek-core", [
    "olympians",
  ]),
  reserve(
    "hyperion",
    "primordial",
    "deity",
    "許珀里翁",
    "Hyperion",
    "greek-core",
    ["olympians"],
  ),
  reserve("theia", "primordial", "deity", "忒亞", "Theia", "greek-core", [
    "olympians",
  ]),
  reserve("themis", "primordial", "deity", "忒彌斯", "Themis", "olympians", [
    "olympians",
  ]),
  reserve(
    "mnemosyne",
    "primordial",
    "deity",
    "姆涅摩緒涅",
    "Mnemosyne",
    "olympians",
    ["olympians"],
  ),
  reserve("phoebe", "primordial", "deity", "福柏", "Phoebe", "greek-core", [
    "olympians",
  ]),
  reserve(
    "prometheus",
    "primordial",
    "deity",
    "普羅米修斯",
    "Prometheus",
    "greek-core",
    ["olympians", "heracles"],
  ),
  reserve(
    "epimetheus",
    "primordial",
    "deity",
    "厄庇墨透斯",
    "Epimetheus",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "pandora",
    "primordial",
    "mortal",
    "潘多拉",
    "Pandora",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "typhon",
    "primordial",
    "creature",
    "堤豐",
    "Typhoeus",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "echidna",
    "primordial",
    "creature",
    "厄客德娜",
    "Echidna",
    "greek-core",
    ["heracles"],
  ),
  reserve(
    "gigantes",
    "primordial",
    "creature",
    "巨人族",
    "Gigantes",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "fire-theft",
    "primordial",
    "event",
    "盜取天火",
    "The Theft of Fire",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "mecone-sacrifice",
    "primordial",
    "event",
    "墨科涅祭肉分配",
    "The Division at Mecone",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "pandora-jar",
    "primordial",
    "event",
    "潘多拉之甕",
    "Pandora's Jar",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "gigantomachy-event",
    "primordial",
    "event",
    "巨人之戰",
    "Gigantomachy",
    "greek-core",
    ["olympians"],
  ),
  reserve(
    "prometheus-bound",
    "primordial",
    "event",
    "普羅米修斯受縛",
    "Prometheus Bound",
    "greek-core",
    ["olympians", "heracles"],
  ),

  // The settled divine household, sky powers and institutions.
  reserve(
    "dionysus",
    "olympus",
    "deity",
    "狄俄倪索斯",
    "Dionysus",
    "olympians",
    ["olympians", "underworld"],
  ),
  reserve("leto", "olympus", "deity", "勒托", "Leto", "olympians", [
    "olympians",
  ]),
  reserve("maia", "olympus", "deity", "瑪亞", "Maia", "olympians", [
    "olympians",
  ]),
  reserve("dione", "olympus", "deity", "狄俄涅", "Dione", "olympians", [
    "olympians",
  ]),
  reserve("hebe", "olympus", "deity", "赫柏", "Hebe", "olympians", [
    "olympians",
    "heracles",
  ]),
  reserve(
    "eileithyia",
    "olympus",
    "deity",
    "厄勒梯亞",
    "Eileithyia",
    "olympians",
    ["olympians", "heracles"],
  ),
  reserve("muses", "olympus", "deity", "繆斯", "Muses", "olympians", [
    "olympians",
  ]),
  reserve("horae", "olympus", "deity", "時序女神", "Horae", "olympians", [
    "olympians",
  ]),
  reserve(
    "charites",
    "olympus",
    "deity",
    "美惠三女神",
    "Charites",
    "olympians",
    ["olympians"],
  ),
  reserve("eurynome", "olympus", "deity", "歐律諾墨", "Eurynome", "olympians", [
    "olympians",
  ]),
  reserve("iris", "olympus", "deity", "伊里斯", "Iris", "olympians", [
    "olympians",
  ]),
  reserve("eos", "olympus", "deity", "厄俄斯", "Eos", "olympians", [
    "olympians",
  ]),
  reserve("selene", "olympus", "deity", "塞勒涅", "Selene", "olympians", [
    "olympians",
  ]),
  reserve("pan", "olympus", "deity", "潘", "Pan", "olympians", ["olympians"]),
  reserve(
    "asclepius",
    "olympus",
    "deity",
    "阿斯克勒庇俄斯",
    "Asclepius",
    "olympians",
    ["olympians"],
  ),
  reserve(
    "olympian-council",
    "olympus",
    "event",
    "奧林匹斯議會",
    "Olympian Council",
    "olympians",
    ["olympians"],
  ),
  reserve(
    "olympian-order-event",
    "olympus",
    "event",
    "奧林匹斯秩序",
    "Olympian Order",
    "olympians",
    ["olympians"],
  ),

  // Mortal and heroic world: Heracles, the Argonaut leaders and their home courts.
  reserve("heracles", "mortal", "hero", "赫拉克勒斯", "Heracles", "heracles", [
    "heracles",
    "argonauts",
    "underworld",
  ]),
  reserve("alcmene", "mortal", "mortal", "阿爾克墨涅", "Alcmene", "heracles", [
    "heracles",
  ]),
  reserve(
    "amphitryon",
    "mortal",
    "mortal",
    "安菲特律翁",
    "Amphitryon",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "eurystheus",
    "mortal",
    "mortal",
    "歐律斯透斯",
    "Eurystheus",
    "heracles",
    ["heracles"],
  ),
  reserve("megara", "mortal", "mortal", "墨伽拉", "Megara", "heracles", [
    "heracles",
    "underworld",
  ]),
  reserve("iolaus", "mortal", "hero", "伊奧勞斯", "Iolaus", "heracles", [
    "heracles",
  ]),
  reserve("abderus", "mortal", "hero", "阿布德羅斯", "Abderus", "heracles", [
    "heracles",
  ]),
  reserve(
    "deianira",
    "mortal",
    "mortal",
    "得伊阿尼拉",
    "Deianira",
    "heracles",
    ["heracles"],
  ),
  reserve("nessus", "mortal", "creature", "涅索斯", "Nessus", "heracles", [
    "heracles",
  ]),
  reserve("augeas", "mortal", "mortal", "奧革阿斯", "Augeas", "heracles", [
    "heracles",
  ]),
  reserve("hippolyta", "mortal", "hero", "希波呂忒", "Hippolyta", "heracles", [
    "heracles",
  ]),
  reserve("amazons", "mortal", "mortal", "亞馬遜人", "Amazons", "heracles", [
    "heracles",
  ]),
  reserve(
    "diomedes-thrace",
    "mortal",
    "hero",
    "色雷斯的狄俄墨得斯",
    "Diomedes of Thrace",
    "heracles",
    ["heracles"],
  ),
  reserve("geryon", "mortal", "creature", "革律翁", "Geryon", "heracles", [
    "heracles",
  ]),
  reserve("omphale", "mortal", "mortal", "翁法勒", "Omphale", "heracles", [
    "heracles",
  ]),
  reserve(
    "nemean-lion",
    "mortal",
    "creature",
    "涅墨亞獅子",
    "Nemean Lion",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "lernaean-hydra",
    "mortal",
    "creature",
    "勒拿九頭蛇",
    "Lernaean Hydra",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "ceryneian-hind",
    "mortal",
    "creature",
    "刻律涅亞牝鹿",
    "Ceryneian Hind",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "erymanthian-boar",
    "mortal",
    "creature",
    "厄律曼托斯野豬",
    "Erymanthian Boar",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "stymphalian-birds",
    "mortal",
    "creature",
    "斯廷法利斯怪鳥",
    "Stymphalian Birds",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "cretan-bull",
    "mortal",
    "creature",
    "克里特公牛",
    "Cretan Bull",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "mares-diomedes",
    "mortal",
    "creature",
    "狄俄墨得斯的牝馬",
    "Mares of Diomedes",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "cattle-geryon",
    "mortal",
    "creature",
    "革律翁的牛群",
    "Cattle of Geryon",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "augean-stables",
    "mortal",
    "place",
    "奧革阿斯牛舍",
    "Augean Stables",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "labours-heracles",
    "mortal",
    "event",
    "海克力士十二試煉",
    "Labours of Heracles",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "apotheosis-heracles",
    "mortal",
    "event",
    "赫拉克勒斯升神",
    "Apotheosis of Heracles",
    "heracles",
    ["heracles"],
  ),
  reserve("jason", "mortal", "hero", "伊阿宋", "Jason", "argonauts", [
    "argonauts",
  ]),
  reserve("medea", "mortal", "mortal", "美狄亞", "Medea", "argonauts", [
    "argonauts",
  ]),
  reserve("pelias", "mortal", "mortal", "珀利阿斯", "Pelias", "argonauts", [
    "argonauts",
  ]),
  reserve("aeson", "mortal", "mortal", "埃宋", "Aeson", "argonauts", [
    "argonauts",
  ]),
  reserve("aeetes", "mortal", "mortal", "埃厄忒斯", "Aeetes", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "chalciope",
    "mortal",
    "mortal",
    "卡爾基俄珀",
    "Chalciope",
    "argonauts",
    ["argonauts"],
  ),
  reserve(
    "absyrtus",
    "mortal",
    "mortal",
    "阿布敘爾托斯",
    "Apsyrtus",
    "argonauts",
    ["argonauts"],
  ),
  reserve("phrixus", "mortal", "hero", "佛里克索斯", "Phrixus", "argonauts", [
    "argonauts",
  ]),
  reserve("helle", "mortal", "mortal", "赫勒", "Helle", "argonauts", [
    "argonauts",
  ]),
  reserve("nephele", "mortal", "deity", "涅斐勒", "Nephele", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "argus-builder",
    "mortal",
    "hero",
    "造船者阿爾戈斯",
    "Argus the Builder",
    "argonauts",
    ["argonauts"],
  ),
  reserve("orpheus", "mortal", "hero", "奧菲斯", "Orpheus", "argonauts", [
    "argonauts",
    "underworld",
  ]),
  reserve("castor", "mortal", "hero", "卡斯托爾", "Castor", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "polydeuces",
    "mortal",
    "hero",
    "波呂丟刻斯",
    "Polydeuces",
    "argonauts",
    ["argonauts"],
  ),
  reserve("zetes", "mortal", "hero", "仄忒斯", "Zetes", "argonauts", [
    "argonauts",
  ]),
  reserve("calais", "mortal", "hero", "卡拉伊斯", "Calais", "argonauts", [
    "argonauts",
  ]),
  reserve("phineus", "mortal", "mortal", "菲紐斯", "Phineus", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "hypsipyle",
    "mortal",
    "mortal",
    "海普西皮勒",
    "Hypsipyle",
    "argonauts",
    ["argonauts"],
  ),
  reserve("hylas", "mortal", "hero", "赫拉斯", "Hylas", "argonauts", [
    "argonauts",
  ]),
  reserve("atalanta", "mortal", "hero", "阿塔蘭忒", "Atalanta", "argonauts", [
    "argonauts",
  ]),
  reserve("chiron", "mortal", "creature", "喀戎", "Chiron", "argonauts", [
    "argonauts",
    "heracles",
  ]),
  reserve("iolcus", "mortal", "place", "伊奧爾科斯", "Iolcus", "argonauts", [
    "argonauts",
  ]),

  // Seas and far shores: voyage geography, maritime powers and world-edge trials.
  reserve("argo", "seas", "event", "阿爾戈號", "Argo", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "golden-fleece",
    "seas",
    "event",
    "金羊毛",
    "Golden Fleece",
    "argonauts",
    ["argonauts"],
  ),
  reserve("colchis", "seas", "place", "科爾喀斯", "Colchis", "argonauts", [
    "argonauts",
  ]),
  reserve("lemnos", "seas", "place", "利姆諾斯", "Lemnos", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "symplegades",
    "seas",
    "place",
    "撞擊岩",
    "Symplegades",
    "argonauts",
    ["argonauts"],
  ),
  reserve(
    "clashing-rocks",
    "seas",
    "event",
    "穿越撞擊岩",
    "Passage of the Clashing Rocks",
    "argonauts",
    ["argonauts"],
  ),
  reserve("harpies", "seas", "creature", "哈耳庇厄", "Harpies", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "dragon-colchis",
    "seas",
    "creature",
    "科爾喀斯守龍",
    "Colchian Dragon",
    "argonauts",
    ["argonauts"],
  ),
  reserve("talos", "seas", "creature", "塔洛斯", "Talos", "argonauts", [
    "argonauts",
  ]),
  reserve("cyzicus", "seas", "hero", "庫濟科斯", "Cyzicus", "argonauts", [
    "argonauts",
  ]),
  reserve("amycus", "seas", "hero", "阿密科斯", "Amycus", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "bebryces",
    "seas",
    "mortal",
    "貝布律刻斯人",
    "Bebryces",
    "argonauts",
    ["argonauts"],
  ),
  reserve(
    "hesperides",
    "seas",
    "deity",
    "赫斯珀里得斯",
    "Hesperides",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "garden-hesperides",
    "seas",
    "place",
    "赫斯珀里得斯花園",
    "Garden of the Hesperides",
    "heracles",
    ["heracles"],
  ),
  reserve("erytheia", "seas", "place", "厄律忒亞", "Erytheia", "heracles", [
    "heracles",
  ]),
  reserve(
    "golden-apples-hesperides",
    "seas",
    "event",
    "取得赫斯珀里得斯金蘋果",
    "Golden Apples of the Hesperides",
    "heracles",
    ["heracles"],
  ),
  reserve(
    "yoking-bulls",
    "seas",
    "event",
    "駕馭噴火公牛",
    "Yoking the Fire-Breathing Bulls",
    "argonauts",
    ["argonauts"],
  ),
  reserve(
    "theft-golden-fleece",
    "seas",
    "event",
    "取走金羊毛",
    "Taking of the Golden Fleece",
    "argonauts",
    ["argonauts"],
  ),
  reserve(
    "argonaut-expedition",
    "seas",
    "event",
    "阿爾戈英雄遠征",
    "Argonaut Expedition",
    "argonauts",
    ["argonauts"],
  ),
  reserve("proteus", "seas", "deity", "普羅透斯", "Proteus", "greek-core", [
    "argonauts",
  ]),
  reserve("triton", "seas", "deity", "特里同", "Triton", "argonauts", [
    "argonauts",
  ]),
  reserve(
    "amphitrite",
    "seas",
    "deity",
    "安菲特里忒",
    "Amphitrite",
    "olympians",
    ["olympians"],
  ),

  // Underworld and Tartarus: rulers, visitors, rivers, judges and exemplary punishments.
  reserve("charon", "underworld", "deity", "卡戎", "Charon", "underworld", [
    "underworld",
  ]),
  reserve(
    "cerberus",
    "underworld",
    "creature",
    "刻耳柏洛斯",
    "Cerberus",
    "underworld",
    ["underworld", "heracles"],
  ),
  reserve("minos", "underworld", "mortal", "米諾斯", "Minos", "underworld", [
    "underworld",
  ]),
  reserve(
    "rhadamanthus",
    "underworld",
    "mortal",
    "拉達曼堤斯",
    "Rhadamanthus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "aeacus",
    "underworld",
    "mortal",
    "埃阿科斯",
    "Aeacus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "sisyphus",
    "underworld",
    "mortal",
    "西西弗斯",
    "Sisyphus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "tantalus",
    "underworld",
    "mortal",
    "坦塔羅斯",
    "Tantalus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "tityus",
    "underworld",
    "mortal",
    "提堤俄斯",
    "Tityus",
    "underworld",
    ["underworld"],
  ),
  reserve("orion", "underworld", "hero", "獵戶俄里翁", "Orion", "underworld", [
    "underworld",
  ]),
  reserve(
    "elpenor",
    "underworld",
    "mortal",
    "埃爾佩諾耳",
    "Elpenor",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "eurydice",
    "underworld",
    "mortal",
    "歐律狄刻",
    "Eurydice",
    "underworld",
    ["underworld"],
  ),
  reserve("theseus", "underworld", "hero", "忒修斯", "Theseus", "underworld", [
    "underworld",
    "heracles",
  ]),
  reserve(
    "pirithous",
    "underworld",
    "hero",
    "皮里托俄斯",
    "Pirithous",
    "underworld",
    ["underworld", "heracles"],
  ),
  reserve(
    "acheron",
    "underworld",
    "place",
    "阿刻戎河",
    "Acheron",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "cocytus",
    "underworld",
    "place",
    "科庫托斯河",
    "Cocytus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "phlegethon",
    "underworld",
    "place",
    "皮里弗勒革同河",
    "Pyriphlegethon",
    "underworld",
    ["underworld"],
  ),
  reserve("lethe", "underworld", "place", "勒忒", "Lethe", "underworld", [
    "underworld",
  ]),
  reserve("elysium", "underworld", "place", "極樂原", "Elysium", "underworld", [
    "underworld",
  ]),
  reserve(
    "asphodel-meadow",
    "underworld",
    "place",
    "阿斯福德爾草原",
    "Asphodel Meadow",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "gates-hades",
    "underworld",
    "place",
    "冥府之門",
    "Gates of Hades",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "taenarum",
    "underworld",
    "place",
    "泰納隆",
    "Taenarum",
    "underworld",
    ["underworld", "heracles"],
  ),
  reserve(
    "erinyes",
    "underworld",
    "deity",
    "厄里倪厄斯",
    "Erinyes",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "ascalaphus",
    "underworld",
    "deity",
    "阿斯卡拉福斯",
    "Ascalaphus",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "judges-dead",
    "underworld",
    "event",
    "死者審判",
    "Judgement of the Dead",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "punishment-dead",
    "underworld",
    "event",
    "冥府刑罰",
    "Punishments in Hades",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "descent-orpheus",
    "underworld",
    "event",
    "奧菲斯下冥府",
    "Orpheus Descends to Hades",
    "underworld",
    ["underworld"],
  ),
  reserve(
    "descent-heracles",
    "underworld",
    "event",
    "赫拉克勒斯下冥府",
    "Heracles Descends to Hades",
    "underworld",
    ["underworld", "heracles"],
  ),
] as const;

export const NORSE_ATLAS_RESERVATIONS: readonly AtlasReservation[] = [
  // Origins, first beings, the world tree and major realms.
  reserve(
    "ginnungagap",
    "world-tree",
    "place",
    "金倫加鴻溝",
    "Ginnungagap",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "ymir",
    "world-tree",
    "creature",
    "尤彌爾",
    "Ymir",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "audhumla",
    "world-tree",
    "creature",
    "歐德姆布拉",
    "Audhumla",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve("buri", "world-tree", "deity", "布里", "Buri", "norse-cosmology", [
    "norse-cosmology",
  ]),
  reserve("borr", "world-tree", "deity", "博爾", "Borr", "norse-cosmology", [
    "norse-cosmology",
  ]),
  reserve(
    "bestla",
    "world-tree",
    "creature",
    "貝絲特拉",
    "Bestla",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve("vili", "world-tree", "deity", "維利", "Vili", "norse-cosmology", [
    "norse-cosmology",
  ]),
  reserve("ve", "world-tree", "deity", "維", "Ve", "norse-cosmology", [
    "norse-cosmology",
  ]),
  reserve("ask", "world-tree", "mortal", "阿斯克", "Ask", "norse-cosmology", [
    "norse-cosmology",
  ]),
  reserve(
    "embla",
    "world-tree",
    "mortal",
    "恩布拉",
    "Embla",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "yggdrasil",
    "world-tree",
    "place",
    "世界樹",
    "Yggdrasil",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "urd-well",
    "world-tree",
    "place",
    "兀兒德之泉",
    "Well of Urd",
    "norse-cosmology",
    ["norse-cosmology", "norse-ragnarok"],
  ),
  reserve(
    "mimir-well",
    "world-tree",
    "place",
    "密米爾之泉",
    "Well of Mimir",
    "norse-cosmology",
    ["norse-cosmology", "norse-divine-order"],
  ),
  reserve(
    "norns",
    "world-tree",
    "deity",
    "諾倫三女神",
    "Norns",
    "norse-cosmology",
    ["norse-cosmology", "norse-ragnarok"],
  ),
  reserve(
    "midgard",
    "world-tree",
    "place",
    "中庭",
    "Midgard",
    "norse-cosmology",
    ["norse-cosmology", "norse-thor"],
  ),
  reserve(
    "niflheim",
    "world-tree",
    "place",
    "尼福爾海姆",
    "Niflheim",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "muspell",
    "world-tree",
    "place",
    "穆斯貝爾",
    "Muspell",
    "norse-cosmology",
    ["norse-cosmology", "norse-ragnarok"],
  ),
  reserve(
    "bifrost",
    "world-tree",
    "place",
    "彩虹橋",
    "Bifrost",
    "norse-cosmology",
    ["norse-cosmology", "norse-ragnarok"],
  ),
  reserve(
    "vanaheim",
    "world-tree",
    "place",
    "華納海姆",
    "Vanaheim",
    "norse-cosmology",
    ["norse-cosmology", "norse-divine-order"],
  ),
  reserve(
    "alfheim",
    "world-tree",
    "place",
    "亞爾夫海姆",
    "Alfheim",
    "norse-cosmology",
    ["norse-cosmology", "norse-divine-order"],
  ),
  reserve(
    "hvergelmir",
    "world-tree",
    "place",
    "赫瓦格密爾",
    "Hvergelmir",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "nidhogg",
    "world-tree",
    "creature",
    "尼德霍格",
    "Nidhogg",
    "norse-cosmology",
    ["norse-cosmology", "norse-ragnarok"],
  ),
  reserve("sol", "world-tree", "deity", "蘇爾", "Sol", "norse-ragnarok", [
    "norse-cosmology",
    "norse-ragnarok",
  ]),
  reserve("mani", "world-tree", "deity", "瑪尼", "Mani", "norse-ragnarok", [
    "norse-cosmology",
    "norse-ragnarok",
  ]),
  reserve(
    "creation-of-worlds",
    "world-tree",
    "event",
    "世界創造",
    "Creation of the Worlds",
    "norse-cosmology",
    ["norse-cosmology"],
  ),
  reserve(
    "creation-of-humans",
    "world-tree",
    "event",
    "人類創造",
    "Creation of Humans",
    "norse-cosmology",
    ["norse-cosmology"],
  ),

  // Divine household, settlement, treasures and fragile order.
  reserve(
    "asgard-place",
    "asgard",
    "place",
    "阿斯加德",
    "Asgard",
    "norse-divine-order",
    ["norse-cosmology", "norse-divine-order"],
  ),
  reserve(
    "valhalla",
    "asgard",
    "place",
    "瓦爾哈拉",
    "Valhalla",
    "norse-divine-order",
    ["norse-divine-order", "norse-ragnarok"],
  ),
  reserve("thor", "asgard", "deity", "托爾", "Thor", "norse-thor", [
    "norse-divine-order",
    "norse-thor",
    "norse-ragnarok",
    "norse-lokasenna",
  ]),
  reserve("frigg", "asgard", "deity", "弗麗嘉", "Frigg", "norse-baldr-loki", [
    "norse-divine-order", "norse-lokasenna",
    "norse-baldr-loki",
  ]),
  reserve(
    "freyja",
    "asgard",
    "deity",
    "芙蕾雅",
    "Freyja",
    "norse-divine-order",
    ["norse-divine-order", "norse-lokasenna"],
  ),
  reserve("freyr", "asgard", "deity", "弗雷", "Freyr", "norse-divine-order", [
    "norse-divine-order", "norse-lokasenna",
    "norse-ragnarok",
  ]),
  reserve(
    "njord",
    "asgard",
    "deity",
    "尼奧爾德",
    "Njord",
    "norse-divine-order",
    ["norse-divine-order", "norse-lokasenna"],
  ),
  reserve(
    "heimdall",
    "asgard",
    "deity",
    "海姆達爾",
    "Heimdall",
    "norse-ragnarok",
    ["norse-divine-order", "norse-ragnarok", "norse-lokasenna"],
  ),
  reserve("baldr", "asgard", "deity", "巴德爾", "Baldr", "norse-baldr-loki", [
    "norse-divine-order",
    "norse-baldr-loki",
    "norse-ragnarok",
  ]),
  reserve("hodr", "asgard", "deity", "霍德爾", "Hodr", "norse-baldr-loki", [
    "norse-baldr-loki",
  ]),
  reserve("bragi", "asgard", "deity", "布拉基", "Bragi", "norse-divine-order", [
    "norse-divine-order",
    "norse-lokasenna",
  ]),
  reserve("idunn", "asgard", "deity", "伊登", "Idunn", "norse-divine-order", [
    "norse-divine-order",
    "norse-lokasenna",
  ]),
  reserve("sif", "asgard", "deity", "希芙", "Sif", "norse-thor", [
    "norse-divine-order",
    "norse-thor",
    "norse-lokasenna",
  ]),
  reserve("ullr", "asgard", "deity", "烏勒爾", "Ullr", "norse-divine-order", [
    "norse-divine-order",
  ]),
  reserve(
    "hermod",
    "asgard",
    "deity",
    "赫爾莫德",
    "Hermod",
    "norse-baldr-loki",
    ["norse-baldr-loki"],
  ),
  reserve("vidar", "asgard", "deity", "維達爾", "Vidar", "norse-ragnarok", [
    "norse-ragnarok",
    "norse-lokasenna",
  ]),
  reserve("vali", "asgard", "deity", "瓦利", "Vali", "norse-baldr-loki", [
    "norse-baldr-loki",
    "norse-ragnarok",
  ]),
  reserve("sigyn", "asgard", "deity", "西格恩", "Sigyn", "norse-baldr-loki", [
    "norse-baldr-loki",
  ]),
  reserve(
    "skirnir",
    "asgard",
    "deity",
    "斯基爾尼爾",
    "Skirnir",
    "norse-divine-order",
    ["binding-of-fenrir", "norse-divine-order"],
  ),
  reserve("mimir", "asgard", "deity", "密米爾", "Mimir", "norse-divine-order", [
    "norse-cosmology",
    "norse-divine-order",
  ]),
  reserve("aegir-lokasenna", "asgard", "deity", "埃吉爾", "Ægir", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("thrud-alvissmol", "asgard", "deity", "特魯德", "Thrud", "norse-alvissmol", ["norse-alvissmol"]),
  reserve("harbard-harbarthsljoth", "asgard", "deity", "哈爾巴德", "Harbard", "norse-harbarthsljoth", ["norse-harbarthsljoth"]),
  reserve("fulla-grimnismol", "asgard", "deity", "芙拉", "Fulla", "norse-grimnismol", ["norse-grimnismol"]),
  reserve("rig-rigsthula", "world-tree", "deity", "里格", "Rig", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("grimnir-between-fires", "asgard", "event", "兩火之間的格里姆尼爾", "Grimnir Between Two Fires", "norse-grimnismol", ["norse-grimnismol"]),
  reserve("hymir-cauldron", "asgard", "artifact", "希米爾的大鍋", "Hymir’s Cauldron", "norse-hymiskvitha", ["norse-hymiskvitha"]),
  reserve("eldir-lokasenna", "asgard", "mortal", "埃爾迪爾", "Eldir", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("fimafeng-lokasenna", "asgard", "mortal", "芬瑪芬格", "Fimafeng", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("byggvir-lokasenna", "asgard", "mortal", "比格維爾", "Byggvir", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("beyla-lokasenna", "asgard", "mortal", "貝拉", "Beyla", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("gefjun-lokasenna", "asgard", "deity", "吉菲恩", "Gefjun", "norse-lokasenna", ["norse-lokasenna"]),
  reserve("nanna", "asgard", "deity", "南娜", "Nanna", "norse-baldr-loki", [
    "norse-baldr-loki",
  ]),
  reserve("thokk", "asgard", "creature", "托克", "Thokk", "norse-baldr-loki", [
    "norse-baldr-loki",
  ]),
  reserve(
    "sleipnir",
    "asgard",
    "creature",
    "斯萊普尼爾",
    "Sleipnir",
    "norse-divine-order",
    ["norse-divine-order", "norse-baldr-loki"],
  ),
  reserve(
    "tanngrisnir-tanngnjostr",
    "asgard",
    "creature",
    "托爾雙山羊",
    "Tanngrisnir and Tanngnjostr",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "mjolnir",
    "asgard",
    "artifact",
    "妙爾尼爾",
    "Mjolnir",
    "norse-thor",
    ["norse-divine-order", "norse-thor"],
  ),
  reserve(
    "gungnir",
    "asgard",
    "artifact",
    "岡格尼爾",
    "Gungnir",
    "norse-divine-order",
    ["norse-divine-order", "norse-ragnarok"],
  ),
  reserve(
    "draupnir",
    "asgard",
    "artifact",
    "德羅普尼爾",
    "Draupnir",
    "norse-divine-order",
    ["norse-divine-order", "norse-baldr-loki"],
  ),
  reserve(
    "brisingamen",
    "asgard",
    "artifact",
    "布里辛加曼",
    "Brisingamen",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "skidbladnir",
    "asgard",
    "artifact",
    "斯基德布拉德尼爾",
    "Skidbladnir",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "gjallarhorn",
    "asgard",
    "artifact",
    "加拉爾號角",
    "Gjallarhorn",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "mistletoe",
    "asgard",
    "artifact",
    "槲寄生",
    "Mistletoe",
    "norse-baldr-loki",
    ["norse-baldr-loki"],
  ),
  reserve(
    "aesir-vanir-settlement",
    "asgard",
    "event",
    "阿斯與華納和解",
    "Aesir-Vanir Settlement",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "treasures-of-gods",
    "asgard",
    "event",
    "諸神寶物",
    "Treasures of the Gods",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "theft-of-mjolnir",
    "asgard",
    "event",
    "妙爾尼爾被盜",
    "Theft of Mjolnir",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "baldr-death",
    "asgard",
    "event",
    "巴德爾之死",
    "Baldr's Death",
    "norse-baldr-loki",
    ["norse-baldr-loki"],
  ),
  reserve(
    "baldr-funeral",
    "asgard",
    "event",
    "巴德爾葬禮",
    "Baldr's Funeral",
    "norse-baldr-loki",
    ["norse-baldr-loki"],
  ),
  reserve(
    "kvasir",
    "asgard",
    "deity",
    "克瓦希爾",
    "Kvasir",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "poetic-mead",
    "asgard",
    "artifact",
    "詩蜜酒",
    "Poetic Mead",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve("rati", "asgard", "artifact", "拉提", "Rati", "norse-poetic-mead", [
    "norse-poetic-mead",
  ]),

  // Giant-world figures, monsters and boundary tests.
  reserve(
    "jotunheim",
    "giant-worlds",
    "place",
    "約頓海姆",
    "Jotunheim",
    "norse-cosmology",
    ["binding-of-fenrir", "norse-cosmology", "norse-thor"],
  ),
  reserve("elivagar-hymiskvitha", "giant-worlds", "place", "伊利伐加爾", "Elivagar", "norse-hymiskvitha", ["norse-hymiskvitha"]),
  reserve("alvis-alvissmol", "giant-worlds", "creature", "阿爾維斯", "Alvis", "norse-alvissmol", ["norse-alvissmol"]),
  reserve("hymir-cup", "giant-worlds", "artifact", "希米爾的酒杯", "Hymir’s Cup", "norse-hymiskvitha", ["norse-hymiskvitha"]),
  reserve("black-ox-head-hymiskvitha", "giant-worlds", "artifact", "黑牛頭", "Black Ox Head", "norse-hymiskvitha", ["norse-hymiskvitha"]),
  reserve("many-headed-pursuers-hymiskvitha", "giant-worlds", "creature", "多頭追兵", "Many-Headed Pursuers", "norse-hymiskvitha", ["norse-hymiskvitha"]),
  reserve(
    "jormungandr",
    "giant-worlds",
    "creature",
    "耶夢加得",
    "Jormungandr",
    "norse-thor",
    ["binding-of-fenrir", "norse-thor", "norse-ragnarok"],
  ),
  reserve("hel", "giant-worlds", "deity", "赫爾", "Hel", "norse-baldr-loki", [
    "binding-of-fenrir",
    "norse-baldr-loki",
  ]),
  reserve(
    "gerd",
    "giant-worlds",
    "creature",
    "格爾德",
    "Gerd",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "skadi",
    "giant-worlds",
    "deity",
    "絲卡蒂",
    "Skadi",
    "norse-divine-order",
    ["norse-divine-order", "norse-lokasenna"],
  ),
  reserve(
    "thrym",
    "giant-worlds",
    "creature",
    "索列姆",
    "Thrym",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "hymir",
    "giant-worlds",
    "creature",
    "希米爾",
    "Hymir",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "utgard-loki",
    "giant-worlds",
    "creature",
    "烏特加德洛基",
    "Utgard-Loki",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "skrymir",
    "giant-worlds",
    "creature",
    "斯克里米爾",
    "Skrymir",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "hrungnir",
    "giant-worlds",
    "creature",
    "赫朗格尼爾",
    "Hrungnir",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "thjazi",
    "giant-worlds",
    "creature",
    "夏基",
    "Thjazi",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "surtr",
    "giant-worlds",
    "creature",
    "蘇爾特爾",
    "Surtr",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "garm",
    "giant-worlds",
    "creature",
    "加姆",
    "Garm",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "thialfi",
    "giant-worlds",
    "mortal",
    "夏爾菲",
    "Thialfi",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "roskva",
    "giant-worlds",
    "mortal",
    "羅斯克瓦",
    "Roskva",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve("logi", "giant-worlds", "creature", "洛吉", "Logi", "norse-thor", [
    "norse-thor",
  ]),
  reserve("hugi", "giant-worlds", "creature", "胡吉", "Hugi", "norse-thor", [
    "norse-thor",
  ]),
  reserve("elli", "giant-worlds", "creature", "埃利", "Elli", "norse-thor", [
    "norse-thor",
  ]),
  reserve(
    "thor-fishing",
    "giant-worlds",
    "event",
    "托爾釣蛇",
    "Thor Fishing for the Serpent",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "utgard-loki-tests",
    "giant-worlds",
    "event",
    "烏特加德洛基試煉",
    "Utgard-Loki's Tests",
    "norse-thor",
    ["norse-thor"],
  ),
  reserve(
    "giant-master-builder",
    "giant-worlds",
    "event",
    "巨人建城者",
    "Giant Master Builder",
    "norse-divine-order",
    ["norse-divine-order"],
  ),
  reserve(
    "fjalar",
    "giant-worlds",
    "creature",
    "費亞拉爾",
    "Fjalar",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "galar",
    "giant-worlds",
    "creature",
    "加拉爾",
    "Galar",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "suttungr",
    "giant-worlds",
    "creature",
    "蘇頓格",
    "Suttungr",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "gunnlod",
    "giant-worlds",
    "creature",
    "岡恩洛德",
    "Gunnlod",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "baugi",
    "giant-worlds",
    "creature",
    "包吉",
    "Baugi",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "hnitbjorg",
    "giant-worlds",
    "place",
    "赫尼特比約格",
    "Hnitbjorg",
    "norse-poetic-mead",
    ["norse-poetic-mead"],
  ),
  reserve(
    "geirrod",
    "giant-worlds",
    "creature",
    "蓋羅德",
    "Geirrod",
    "norse-geirrod-journey",
    ["norse-geirrod-journey"],
  ),
  reserve(
    "gridr",
    "giant-worlds",
    "creature",
    "格里德",
    "Gridr",
    "norse-geirrod-journey",
    ["norse-geirrod-journey"],
  ),
  reserve(
    "vimur",
    "giant-worlds",
    "place",
    "維穆爾河",
    "Vimur",
    "norse-geirrod-journey",
    ["norse-geirrod-journey"],
  ),
  reserve(
    "gjalp",
    "giant-worlds",
    "creature",
    "加爾普",
    "Gjalp",
    "norse-geirrod-journey",
    ["norse-geirrod-journey"],
  ),
  reserve(
    "greip",
    "giant-worlds",
    "creature",
    "格雷普",
    "Greip",
    "norse-geirrod-journey",
    ["norse-geirrod-journey"],
  ),

  // Fenrir binding details held as future slots, not promoted facts.
  reserve(
    "laeding",
    "binding-places",
    "artifact",
    "萊丁格",
    "Laedingr",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "dromi",
    "binding-places",
    "artifact",
    "德羅米",
    "Dromi",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "gelgja",
    "binding-places",
    "artifact",
    "格爾加",
    "Gelgja",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "gjoll-binding-stone",
    "binding-places",
    "artifact",
    "格約爾束石",
    "Gjoll Binding Stone",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "fenrir-mouth-sword",
    "binding-places",
    "artifact",
    "芬里爾口中之劍",
    "Sword in Fenrir's Mouth",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "thviti",
    "binding-places",
    "place",
    "斯維提",
    "Thviti",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),
  reserve(
    "van-river",
    "binding-places",
    "place",
    "Ván 河",
    "River Van",
    "norse-fenrir-extension",
    ["binding-of-fenrir"],
  ),

  // Doom, underworld horizon and post-Ragnarok renewal.
  reserve(
    "hel-realm",
    "doom-horizon",
    "place",
    "赫爾冥界",
    "Realm of Hel",
    "norse-baldr-loki",
    ["norse-baldr-loki", "norse-ragnarok"],
  ),
  reserve(
    "fimbulwinter",
    "doom-horizon",
    "event",
    "芬布爾之冬",
    "Fimbulwinter",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "skoll",
    "doom-horizon",
    "creature",
    "斯庫爾",
    "Skoll",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "hati",
    "doom-horizon",
    "creature",
    "哈提",
    "Hati",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "vigrid",
    "doom-horizon",
    "place",
    "維格利德",
    "Vigrid",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "naglfar",
    "doom-horizon",
    "artifact",
    "納格爾法",
    "Naglfar",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "hrym",
    "doom-horizon",
    "creature",
    "赫里姆",
    "Hrym",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "loki-punishment",
    "doom-horizon",
    "event",
    "洛基受罰",
    "Loki's Punishment",
    "norse-baldr-loki",
    ["norse-baldr-loki", "norse-ragnarok"],
  ),
  reserve(
    "vidar-avenges-odin",
    "doom-horizon",
    "event",
    "維達爾為奧丁復仇",
    "Vidar Avenges Odin",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "renewal-after-ragnarok",
    "doom-horizon",
    "event",
    "諸神黃昏後的新生",
    "Renewal after Ragnarok",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),
  reserve(
    "lif-and-lifthrasir",
    "doom-horizon",
    "mortal",
    "利夫與利夫特拉西爾",
    "Lif and Lifthrasir",
    "norse-ragnarok",
    ["norse-ragnarok"],
  ),

  // Heroic lays and Volsung saga layer; keep separate from the divine core route.
  reserve(
    "sigurd",
    "heroic-lays",
    "hero",
    "西格爾德",
    "Sigurd",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "gripir",
    "heroic-lays",
    "mortal",
    "格里皮爾",
    "Gripir",
    "norse-gripisspo",
    ["norse-gripisspo"],
  ),
  reserve("svipdag", "heroic-lays", "hero", "斯維普達格", "Svipdag", "norse-svipdagsmol", ["norse-svipdagsmol"]),
  reserve("groa-svipdag", "heroic-lays", "mortal", "格羅阿", "Groa", "norse-svipdagsmol", ["norse-svipdagsmol"]),
  reserve("fjolsvith-svipdag", "heroic-lays", "creature", "菲奧爾斯維德", "Fjolsvith", "norse-svipdagsmol", ["norse-svipdagsmol"]),
  reserve("mengloth-svipdag", "heroic-lays", "mortal", "孟格洛斯", "Mengloth", "norse-svipdagsmol", ["norse-svipdagsmol"]),
  reserve("frodi-grottasongr", "heroic-lays", "mortal", "弗羅迪", "Frodi", "norse-grottasongr", ["norse-grottasongr"]),
  reserve("fenja-grottasongr", "heroic-lays", "creature", "芬雅", "Fenja", "norse-grottasongr", ["norse-grottasongr"]),
  reserve("menja-grottasongr", "heroic-lays", "creature", "曼雅", "Menja", "norse-grottasongr", ["norse-grottasongr"]),
  reserve("grotti-mill", "heroic-lays", "artifact", "格羅蒂", "Grotti", "norse-grottasongr", ["norse-grottasongr"]),
  reserve(
    "sigmund",
    "heroic-lays",
    "hero",
    "西格蒙德",
    "Sigmund",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "volsung",
    "heroic-lays",
    "hero",
    "沃爾松",
    "Volsung",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "brynhild",
    "heroic-lays",
    "hero",
    "布倫希爾德",
    "Brynhild",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "gudrun",
    "heroic-lays",
    "mortal",
    "古德倫",
    "Gudrun",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "hjordis",
    "heroic-lays",
    "mortal",
    "希奧迪絲",
    "Hjordis",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "regin",
    "heroic-lays",
    "creature",
    "雷金",
    "Regin",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "fafnir",
    "heroic-lays",
    "creature",
    "法夫尼爾",
    "Fafnir",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "hreidmar",
    "heroic-lays",
    "creature",
    "赫雷德瑪爾",
    "Hreidmar",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve("otr", "heroic-lays", "creature", "奧特爾", "Otr", "norse-volsungs", [
    "norse-volsungs",
  ]),
  reserve(
    "andvari",
    "heroic-lays",
    "creature",
    "安德瓦利",
    "Andvari",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve("giuki", "heroic-lays", "mortal", "久基", "Giuki", "norse-volsungs", [
    "norse-volsungs",
  ]),
  reserve(
    "gunnar",
    "heroic-lays",
    "hero",
    "貢納爾",
    "Gunnar",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve("hogni", "heroic-lays", "hero", "霍格尼", "Hogni", "norse-volsungs", [
    "norse-volsungs",
  ]),
  reserve("atli", "heroic-lays", "mortal", "阿特利", "Atli", "norse-volsungs", [
    "norse-volsungs",
  ]),
  reserve(
    "grani",
    "heroic-lays",
    "creature",
    "格拉尼",
    "Grani",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "gram",
    "heroic-lays",
    "artifact",
    "格拉姆",
    "Gram",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "andvaranaut",
    "heroic-lays",
    "artifact",
    "安德瓦拉瑙特",
    "Andvaranaut",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "cursed-gold",
    "heroic-lays",
    "artifact",
    "受詛黃金",
    "Cursed Gold",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "fafnir-slaying",
    "heroic-lays",
    "event",
    "殺死法夫尼爾",
    "Slaying of Fafnir",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  reserve(
    "sigurd-death",
    "heroic-lays",
    "event",
    "西格爾德之死",
    "Sigurd's Death",
    "norse-volsungs",
    ["norse-volsungs"],
  ),
  // Völundarkviða is a distinct heroic lay; its setting stays explicitly unlocatable.
  reserve("volund", "heroic-lays", "hero", "沃倫德", "Volund", "norse-volund", [
    "norse-volund",
  ]),
  reserve(
    "egil-volund",
    "heroic-lays",
    "hero",
    "埃吉爾",
    "Egil",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "slagfith",
    "heroic-lays",
    "hero",
    "斯拉格菲德",
    "Slagfith",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "alvit",
    "heroic-lays",
    "creature",
    "阿爾維特",
    "Alvit",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "olrun",
    "heroic-lays",
    "creature",
    "奧爾倫",
    "Olrun",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "svanhvit",
    "heroic-lays",
    "creature",
    "斯萬維特",
    "Svanhvit",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "nidud",
    "heroic-lays",
    "mortal",
    "尼杜德",
    "Nithud",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "bodvild",
    "heroic-lays",
    "mortal",
    "博德維爾德",
    "Bodvild",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "niduds-sons",
    "heroic-lays",
    "mortal",
    "尼杜德的兩個兒子",
    "Nithud's Two Sons",
    "norse-volund",
    ["norse-volund"],
  ),
  reserve(
    "volund-isle",
    "heroic-lays",
    "place",
    "沃倫德的島上工坊",
    "Volund's Island Workshop",
    "norse-volund",
    ["norse-volund"],
  ),
  // Helgakvitha Hundingsbana I remains a parallel heroic lay; its named places are deliberately unlocatable.
  reserve(
    "helgi-hundingsbane",
    "heroic-lays",
    "hero",
    "赫爾吉・洪丁斯巴尼",
    "Helgi Hundingsbane",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "borghild-helgi",
    "heroic-lays",
    "mortal",
    "博爾希爾德",
    "Borghild",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "hunding",
    "heroic-lays",
    "mortal",
    "洪丁",
    "Hunding",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "hundings-sons",
    "heroic-lays",
    "mortal",
    "洪丁諸子",
    "Hunding's Sons",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "sigrun",
    "heroic-lays",
    "hero",
    "西格倫",
    "Sigrun",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "hogni-sigrun",
    "heroic-lays",
    "mortal",
    "霍格尼（西格倫之父）",
    "Hogni, Sigrun's Father",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "hothbrodd",
    "heroic-lays",
    "mortal",
    "霍特布羅德",
    "Hothbrodd",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "granmar",
    "heroic-lays",
    "mortal",
    "格蘭馬爾",
    "Granmar",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "gothmund",
    "heroic-lays",
    "mortal",
    "戈特蒙德",
    "Gothmund",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "sinfjotli",
    "heroic-lays",
    "hero",
    "辛菲奧特利",
    "Sinfjotli",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "logafjoll",
    "heroic-lays",
    "place",
    "洛加菲厄爾",
    "Logafjoll",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  reserve(
    "frekastein",
    "heroic-lays",
    "place",
    "弗雷卡斯坦",
    "Frekastein",
    "norse-helgi-hundingsbane",
    ["norse-helgi-hundingsbane"],
  ),
  // The Hjorvarthsson witness is deliberately source-scoped beside, not merged into, Helgi Hundingsbane.
  reserve("hjorvarth-helgi", "heroic-lays", "mortal", "希約爾瓦德", "Hjorvarth", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("sigrlin-hjorvarth", "heroic-lays", "mortal", "西格琳", "Sigrlin", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("svafnir-svavaland", "heroic-lays", "mortal", "斯瓦夫尼爾", "Svafnir", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("hrothmar-svavaland", "heroic-lays", "mortal", "赫羅斯馬爾", "Hrothmar", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("helgi-hjorvarthsson", "heroic-lays", "hero", "赫爾吉・希約爾瓦德松", "Helgi Hjorvarthsson", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("svava-hjorvarth", "heroic-lays", "hero", "斯瓦娃", "Svava", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("hethin-hjorvarth", "heroic-lays", "hero", "赫辛", "Hethin", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("eylimi-svava", "heroic-lays", "mortal", "埃利米", "Eylimi", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("hati-hatafjord", "heroic-lays", "creature", "哈提（哈塔峽灣）", "Hati", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("hrimgerth-hatafjord", "heroic-lays", "creature", "赫里姆蓋爾德", "Hrimgerth", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("alf-hrothmar", "heroic-lays", "mortal", "阿爾夫（赫羅斯馬爾之子）", "Alf", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("sigar-hjorvarth", "heroic-lays", "mortal", "西加爾", "Sigar", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("svavaland-hjorvarth", "heroic-lays", "place", "斯瓦瓦蘭", "Svavaland", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("frekastein-hjorvarth", "heroic-lays", "place", "弗雷卡斯坦（希約爾瓦德松傳統）", "Frekastein", "norse-helgi-hjorvarthsson", ["norse-helgi-hjorvarthsson"]),
  reserve("vafthruthnir-contest", "giant-worlds", "creature", "瓦夫蘇魯德尼爾", "Vafthruthnir", "norse-vafthruthnismol", ["norse-vafthruthnismol"]),
  reserve("bergelmir-vafthruthnismol", "giant-worlds", "creature", "貝爾格爾米爾", "Bergelmir", "norse-vafthruthnismol", ["norse-vafthruthnismol"]),
  reserve("hraesvelg-vafthruthnismol", "giant-worlds", "creature", "赫瑞斯維爾格", "Hraesvelg", "norse-vafthruthnismol", ["norse-vafthruthnismol"]),
  reserve("ifing-vafthruthnismol", "giant-worlds", "place", "伊芬河", "Ifing", "norse-vafthruthnismol", ["norse-vafthruthnismol"]),
  reserve("vingi-atlamol", "heroic-lays", "mortal", "文吉", "Vingi", "norse-atlamol", ["norse-atlamol"]),
  reserve("kostbera-atlamol", "heroic-lays", "mortal", "科斯特貝拉", "Kostbera", "norse-atlamol", ["norse-atlamol"]),
  reserve("glaumvor-atlamol", "heroic-lays", "mortal", "格勞姆沃爾", "Glaumvor", "norse-atlamol", ["norse-atlamol"]),
  reserve("snaever-atlamol", "heroic-lays", "hero", "斯奈瓦爾", "Snaever", "norse-atlamol", ["norse-atlamol"]),
  reserve("solar-atlamol", "heroic-lays", "hero", "索拉爾", "Solar", "norse-atlamol", ["norse-atlamol"]),
  reserve("hniflung-atlamol", "heroic-lays", "hero", "赫尼夫隆", "Hniflung", "norse-atlamol", ["norse-atlamol"]),
  reserve("tyrfing-hervor", "heroic-lays", "artifact", "提爾鋒", "Tyrfing", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("arngrim-tyrfing", "heroic-lays", "hero", "阿恩格里姆", "Arngrim", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("eyfura-tyrfing", "heroic-lays", "mortal", "埃芙拉", "Eyfura", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("angantyr-tyrfing", "heroic-lays", "hero", "安甘蒂爾", "Angantyr", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("hjalmar-tyrfing", "heroic-lays", "hero", "赫亞爾馬爾", "Hjalmar", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("orvar-odd-tyrfing", "heroic-lays", "hero", "奧爾瓦爾・奧德", "Orvar-Odd", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("hervor-tyrfing", "heroic-lays", "hero", "赫爾沃爾", "Hervor", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("samsey-tyrfing", "heroic-lays", "place", "薩姆瑟島", "Samsey", "norse-hervor-awakening", ["norse-hervor-awakening"]),
  reserve("hyndla-hyndluljoth", "heroic-lays", "creature", "海因德拉", "Hyndla", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("ai-rigsthula", "world-tree", "mortal", "艾", "Ai", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("edda-rigsthula", "world-tree", "mortal", "埃達", "Edda", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("thrall-rigsthula", "world-tree", "mortal", "特雷爾", "Thrall", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("thir-rigsthula", "world-tree", "mortal", "西爾", "Thir", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("karl-rigsthula", "world-tree", "mortal", "卡爾", "Karl", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("snor-rigsthula", "world-tree", "mortal", "斯諾爾", "Snor", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("jarl-rigsthula", "world-tree", "mortal", "雅爾", "Jarl", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("erna-rigsthula", "world-tree", "mortal", "艾爾娜", "Erna", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("kon-rigsthula", "world-tree", "mortal", "青年康", "Young Kon", "norse-rigsthula", ["norse-rigsthula"]),
  reserve("gjaflaug-guthrun-i", "heroic-lays", "mortal", "賈夫勞格", "Gjaflaug", "norse-guthrunarkvitha-i", ["norse-guthrunarkvitha-i"]),
  reserve("herborg-guthrun-i", "heroic-lays", "mortal", "赫爾堡", "Herborg", "norse-guthrunarkvitha-i", ["norse-guthrunarkvitha-i"]),
  reserve("gollrond-guthrun-i", "heroic-lays", "mortal", "戈爾倫德", "Gollrond", "norse-guthrunarkvitha-i", ["norse-guthrunarkvitha-i"]),
  reserve("gudrun-at-sigurd-body-guthrun-i", "heroic-lays", "event", "西古爾德遺體前的古德倫", "Guthrun at Sigurd’s Body", "norse-guthrunarkvitha-i", ["norse-guthrunarkvitha-i"]),
  reserve("gotthorm-sigurth-short", "heroic-lays", "hero", "戈特霍姆", "Gotthorm", "norse-sigurtharkvitha-en-skamma", ["norse-sigurtharkvitha-en-skamma"]),
  reserve("rock-giantess-helreith", "heroic-lays", "creature", "岩居女巨人", "Rock-Dwelling Giantess", "norse-helreith-brynhildar", ["norse-helreith-brynhildar"]),
  reserve("hjalmgunnar-helreith", "heroic-lays", "hero", "希亞爾姆貢納爾", "Hjalmgunnar", "norse-helreith-brynhildar", ["norse-helreith-brynhildar"]),
  reserve("brynhild-hel-road", "heroic-lays", "event", "布琳希爾德的赫爾路", "Brynhild’s Hel-Road", "norse-helreith-brynhildar", ["norse-helreith-brynhildar"]),
  reserve("grimhild-brot", "heroic-lays", "mortal", "格里姆希爾德", "Grimhild", "norse-brot-sigurtharkvithu", ["norse-brot-sigurtharkvithu"]),
  reserve("raven-brot", "heroic-lays", "creature", "預言烏鴉", "Prophetic Raven", "norse-brot-sigurtharkvithu", ["norse-brot-sigurtharkvithu"]),
  reserve("outdoor-sigurd-death-brot", "heroic-lays", "event", "萊茵河以南的戶外殺局", "The Outdoor Killing South of the Rhine", "norse-brot-sigurtharkvithu", ["norse-brot-sigurtharkvithu"]),
  reserve("thjothrek-guthrun-ii", "heroic-lays", "hero", "提奧達雷克", "Thjothrek", "norse-guthrunarkvitha-ii", ["norse-guthrunarkvitha-ii"]),
  reserve("thora-guthrun-ii", "heroic-lays", "mortal", "索拉", "Thora", "norse-guthrunarkvitha-ii", ["norse-guthrunarkvitha-ii"]),
  reserve("atli-dream-guthrun-ii", "heroic-lays", "event", "阿特利的不祥夢", "Atli’s Ominous Dream", "norse-guthrunarkvitha-ii", ["norse-guthrunarkvitha-ii"]),
  reserve("herkja-guthrun-iii", "heroic-lays", "mortal", "赫爾嘉", "Herkja", "norse-guthrunarkvitha-iii", ["norse-guthrunarkvitha-iii"]),
  reserve("saxi-guthrun-iii", "heroic-lays", "mortal", "薩克西", "Saxi", "norse-guthrunarkvitha-iii", ["norse-guthrunarkvitha-iii"]),
  reserve("boiling-water-ordeal-guthrun-iii", "heroic-lays", "event", "沸水試煉", "The Boiling-Water Ordeal", "norse-guthrunarkvitha-iii", ["norse-guthrunarkvitha-iii"]),
  reserve("oddrun", "heroic-lays", "mortal", "奧德倫", "Oddrun", "norse-oddrunargratr", ["norse-oddrunargratr"]),
  reserve("borgny", "heroic-lays", "mortal", "博爾格尼", "Borgny", "norse-oddrunargratr", ["norse-oddrunargratr"]),
  reserve("vilmund", "heroic-lays", "mortal", "維爾蒙德", "Vilmund", "norse-oddrunargratr", ["norse-oddrunargratr"]),
  reserve("atli-mother-oddrun", "heroic-lays", "mortal", "阿特利之母", "Atli’s Mother", "norse-oddrunargratr", ["norse-oddrunargratr"]),
  reserve("knefroth-atlakvitha", "heroic-lays", "mortal", "克內弗羅特", "Knefröth", "norse-atlakvitha", ["norse-atlakvitha"]),
  reserve("hjalli-atlakvitha", "heroic-lays", "mortal", "赫亞利", "Hjalli", "norse-atlakvitha", ["norse-atlakvitha"]),
  reserve("erp-atlakvitha", "heroic-lays", "mortal", "厄爾普", "Erp", "norse-atlakvitha", ["norse-atlakvitha"]),
  reserve("eitil-atlakvitha", "heroic-lays", "mortal", "埃提爾", "Eitil", "norse-atlakvitha", ["norse-atlakvitha"]),
  reserve("jonak-guthrunarhvot", "heroic-lays", "mortal", "約納克", "Jonak", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("sorli-guthrunarhvot", "heroic-lays", "mortal", "索利", "Sorli", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("erp-jonak-guthrunarhvot", "heroic-lays", "mortal", "厄爾普", "Erp", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("hamther-guthrunarhvot", "heroic-lays", "mortal", "哈姆瑟", "Hamther", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("svanhild-guthrunarhvot", "heroic-lays", "mortal", "斯萬希爾德", "Svanhild", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("jormunrek-guthrunarhvot", "heroic-lays", "mortal", "約爾蒙雷克", "Jormunrek", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("bikki-guthrunarhvot", "heroic-lays", "mortal", "比基", "Bikki", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("randver-guthrunarhvot", "heroic-lays", "mortal", "蘭德維爾", "Randver", "norse-guthrunarhvot", ["norse-guthrunarhvot"]),
  reserve("dag-helgi-ii", "heroic-lays", "mortal", "達格", "Dag", "norse-helgakvitha-hundingsbana-ii", ["norse-helgakvitha-hundingsbana-ii"]),
  reserve("fjoturlund-helgi-ii", "heroic-lays", "place", "費約圖爾林德", "Fjoturlund", "norse-helgakvitha-hundingsbana-ii", ["norse-helgakvitha-hundingsbana-ii"]),
  reserve("helgi-barrow-helgi-ii", "heroic-lays", "event", "赫爾吉的墓丘夜訪", "Helgi’s Mound Visit", "norse-helgakvitha-hundingsbana-ii", ["norse-helgakvitha-hundingsbana-ii"]),
  reserve("hrauthung-grimnismol", "heroic-lays", "mortal", "赫勞通", "Hrauthung", "norse-grimnismol", ["norse-grimnismol"]),
  reserve("agnar-grimnismol", "heroic-lays", "hero", "阿格納", "Agnar", "norse-grimnismol", ["norse-grimnismol"]),
  reserve("geirroth-grimnismol", "heroic-lays", "mortal", "格伊爾羅德", "Geirröth", "norse-grimnismol", ["norse-grimnismol"]),
  reserve("ottar-hyndluljoth", "heroic-lays", "hero", "奧塔爾", "Ottar", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("hildisvini-hyndluljoth", "heroic-lays", "creature", "希爾迪斯維尼", "Hildisvini", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("instein-hyndluljoth", "heroic-lays", "mortal", "因斯坦", "Instein", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("alf-old-hyndluljoth", "heroic-lays", "mortal", "老阿爾夫", "Alf the Old", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("ulf-hyndluljoth", "heroic-lays", "mortal", "烏爾夫", "Ulf", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("saefari-hyndluljoth", "heroic-lays", "mortal", "賽法里", "Saefari", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("svan-red-hyndluljoth", "heroic-lays", "mortal", "紅髮斯萬", "Svan the Red", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("hledis-hyndluljoth", "heroic-lays", "mortal", "赫萊迪絲", "Hledis", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("frothi-hyndluljoth", "heroic-lays", "mortal", "弗羅西", "Frothi", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("friaut-hyndluljoth", "heroic-lays", "mortal", "弗里奧特", "Friaut", "norse-hyndluljoth", ["norse-hyndluljoth"]),
  reserve("ottar-ancestry-wager", "heroic-lays", "event", "奧塔爾的家系賭注", "Ottar’s Ancestry Wager", "norse-hyndluljoth", ["norse-hyndluljoth"]),
] as const;

const reservationIds = new Set(ATLAS_RESERVATIONS.map((item) => item.id));
if (reservationIds.size !== ATLAS_RESERVATIONS.length)
  throw new Error("Atlas reservations contain duplicate IDs");

const currentNodeIdsByDataset: Readonly<
  Record<AtlasDatasetKey, Readonly<Record<string, readonly string[]>>>
> = {
  greek: greekCurrentNodeIdsByRegion,
  norse: norseCurrentNodeIdsByRegion,
};

const reservationsByDataset: Readonly<
  Record<AtlasDatasetKey, readonly AtlasReservation[]>
> = {
  greek: ATLAS_RESERVATIONS,
  norse: NORSE_ATLAS_RESERVATIONS,
};

const defaultRegionByDataset: Readonly<Record<AtlasDatasetKey, AtlasRegionId>> =
  {
    greek: "mortal",
    norse: "world-tree",
  };

function datasetKeyFor(dataset?: string): AtlasDatasetKey {
  return dataset?.toLowerCase().includes("norse") ? "norse" : "greek";
}

function buildCurrentRegionById(datasetKey: AtlasDatasetKey) {
  const currentRegionById = new Map<string, AtlasRegionId>();
  Object.entries(currentNodeIdsByDataset[datasetKey]).forEach(
    ([regionId, ids]) =>
      ids.forEach((id) => {
        if (currentRegionById.has(id))
          throw new Error(
            `Atlas current node ${id} has more than one home region`,
          );
        currentRegionById.set(id, regionId as AtlasRegionId);
      }),
  );
  return currentRegionById;
}

const currentRegionByDataset = {
  greek: buildCurrentRegionById("greek"),
  norse: buildCurrentRegionById("norse"),
} satisfies Record<AtlasDatasetKey, Map<string, AtlasRegionId>>;

Object.entries(reservationsByDataset).forEach(([datasetKey, reservations]) => {
  const currentRegionById =
    currentRegionByDataset[datasetKey as AtlasDatasetKey];
  const ids = new Set(reservations.map((item) => item.id));
  if (ids.size !== reservations.length)
    throw new Error(
      `Atlas reservations contain duplicate IDs for ${datasetKey}`,
    );
  reservations.forEach((item) => {
    if (currentRegionById.has(item.id))
      throw new Error(
        `Atlas reservation ${item.id} overlaps a current canonical node`,
      );
  });
});

export function atlasRegionIdsForDataset(
  dataset?: string,
): readonly AtlasRegionId[] {
  return datasetKeyFor(dataset) === "norse"
    ? norseAtlasRegionIds
    : greekAtlasRegionIds;
}

export function atlasReservationsForDataset(
  dataset?: string,
): readonly AtlasReservation[] {
  return reservationsByDataset[datasetKeyFor(dataset)];
}

export function defaultAtlasRegionForDataset(dataset?: string): AtlasRegionId {
  return defaultRegionByDataset[datasetKeyFor(dataset)];
}

export function atlasRegionForId(
  id: string,
  dataset?: string,
): AtlasRegionId | undefined {
  const datasetKey = datasetKeyFor(dataset);
  return (
    currentRegionByDataset[datasetKey].get(id) ??
    reservationsByDataset[datasetKey].find((item) => item.id === id)?.regionId
  );
}

export function atlasSlotIdsForRegion(
  regionId: AtlasRegionId,
  dataset?: string,
) {
  const datasetKey = datasetKeyFor(dataset);
  const slotIds = [
    ...(currentNodeIdsByDataset[datasetKey][regionId] ?? []),
    ...reservationsByDataset[datasetKey]
      .filter((item) => item.regionId === regionId)
      .map((item) => item.id),
  ];
  const leadSlotIds = atlasLeadSlotIdsByDataset[datasetKey][regionId] ?? [];
  const slotIdSet = new Set(slotIds);
  return [
    ...leadSlotIds.filter((id) => slotIdSet.has(id)),
    ...slotIds.filter((id) => !leadSlotIds.includes(id)),
  ];
}

export function currentAtlasNodeIds(dataset?: string) {
  return [...currentRegionByDataset[datasetKeyFor(dataset)].keys()];
}
